import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { API, logErr } from "../lib/api";
import { useAuth } from "./AuthContext";

const CartCtx = createContext(null);
const LS_KEY = "dn_cart";

function baseItems(items) {
  return items.map(({ product_id, size, color, qty }) => ({ product_id, size, color, qty }));
}

async function enrichItems(list) {
  const enriched = await Promise.all(list.map(async (i) => {
    try {
      const p = (await API.get(`/products/${i.product_id}`)).data;
      return { ...i, name: p.name, slug: p.slug, image: p.images[0], price: p.sale_price || p.price };
    } catch { return null; }
  }));
  return enriched.filter(Boolean);
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; } catch { return []; }
  });
  const [pop, setPop] = useState(false);
  const merged = useRef(false);

  // Load server cart when user logs in; merge guest items. Guests: enrich IDs-only cart from API.
  useEffect(() => {
    if (!user) {
      merged.current = false;
      if (items.length && !items[0].name) enrichItems(baseItems(items)).then(setItems);
      return;
    }
    (async () => {
      try {
        const r = await API.get("/cart");
        let serverItems = r.data.items || [];
        const guest = merged.current ? [] : items;
        if (guest.length) {
          const map = new Map();
          [...serverItems, ...baseItems(guest)].forEach((i) => {
            const k = `${i.product_id}|${i.size}|${i.color}`;
            const existing = map.get(k);
            map.set(k, existing ? { ...existing, qty: existing.qty + i.qty } : { ...i });
          });
          serverItems = [...map.values()];
          await API.put("/cart", { items: serverItems });
        }
        merged.current = true;
        setItems(await enrichItems(serverItems));
        localStorage.removeItem(LS_KEY);
      } catch (err) {
        logErr("Failed to sync cart:", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Persist — guests store only item IDs/variants; details always fetched fresh from the API
  useEffect(() => {
    if (user === null) return;
    if (user) {
      if (merged.current) API.put("/cart", { items: baseItems(items) }).catch((err) => logErr("Failed to save cart:", err));
    } else {
      localStorage.setItem(LS_KEY, JSON.stringify(baseItems(items)));
    }
  }, [items, user]);

  const addItem = useCallback((product, size, color, qty = 1) => {
    setItems((prev) => {
      const k = (i) => i.product_id === product.id && i.size === size && i.color === color;
      const ex = prev.find(k);
      if (ex) return prev.map((i) => (k(i) ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { product_id: product.id, name: product.name, slug: product.slug,
        image: product.images[0], price: product.sale_price || product.price, size, color, qty }];
    });
    setPop(true);
    setTimeout(() => setPop(false), 450);
  }, []);

  const updateQty = useCallback((item, qty) => {
    if (qty <= 0) return setItems((p) => p.filter((i) => !(i.product_id === item.product_id && i.size === item.size && i.color === item.color)));
    setItems((p) => p.map((i) => (i.product_id === item.product_id && i.size === item.size && i.color === item.color ? { ...i, qty } : i)));
  }, []);

  const removeItem = useCallback((item) => {
    setItems((p) => p.filter((i) => !(i.product_id === item.product_id && i.size === item.size && i.color === item.color)));
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    if (user) API.delete("/cart").catch(() => {});
  }, [user]);

  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartCtx.Provider value={{ items, addItem, updateQty, removeItem, clear, subtotal, count, pop }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
